import { convertInternalHref } from '../internal-dashboard/routing.js';
import type { ProofMetric, ProofMetricStatus } from '../../types/customer-ops.js';

const URL_BASE = 'https://admin.local';

function startOfToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

function startOfExpirationDate(value: string): Date | null {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    const year = Number(dateOnly[1]);
    const month = Number(dateOnly[2]);
    const day = Number(dateOnly[3]);
    const parsed = new Date(year, month - 1, day);
    if (
      parsed.getFullYear() !== year
      || parsed.getMonth() !== month - 1
      || parsed.getDate() !== day
    ) return null;
    parsed.setHours(0, 0, 0, 0);
    return parsed;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
}

export function isProofExpirationPast(row: Pick<ProofMetric, 'expires_at'>): boolean {
  if (!row.expires_at) return false;
  const expires = startOfExpirationDate(row.expires_at);
  return !expires || expires < startOfToday();
}

export function isProofMetricExpired(row: ProofMetric): boolean {
  if (row.status !== 'approved' && row.status !== 'ready') return false;
  return isProofExpirationPast(row);
}

/** Display-only status; DB row may still be `approved` (no cron in Phase 1). */
export function getProofMetricDisplayStatus(row: ProofMetric): ProofMetricStatus {
  if (isProofMetricExpired(row)) {
    return 'expired';
  }
  return row.status;
}

export function proofMetricIdFromUrl(url: string): string {
  try {
    const pathname = new URL(url, typeof window === 'undefined' ? URL_BASE : window.location.origin).pathname;
    const slug = pathname.split('/').filter(Boolean).pop() ?? 'brief';
    const normalized = slug.replace(/\.[a-z0-9]+$/i, '').replace(/[^a-z0-9]+/gi, '-');
    return `PROOF-${normalized.toUpperCase().slice(0, 48)}`;
  } catch {
    return `PROOF-${Date.now()}`;
  }
}

export function labelFromUrl(url: string): string {
  try {
    const pathname = new URL(url, typeof window === 'undefined' ? URL_BASE : window.location.origin).pathname;
    const slug = pathname.split('/').filter(Boolean).pop() ?? 'Performance brief';
    return slug
      .replace(/\.[a-z0-9]+$/i, '')
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  } catch {
    return 'Performance brief';
  }
}

export function isSafeProofSourceRef(value: string): boolean {
  if (value.startsWith('/') && !value.startsWith('//')) return true;

  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

export function normalizeProofSourceRef(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error('Source URL is required.');
  }
  if (!isSafeProofSourceRef(trimmed)) {
    throw new Error('Source URL must start with https://, http://, or /.');
  }
  return trimmed;
}

export function safeProofSourceHref(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (trimmed === '/proof-source-withheld') return null;
  if (!trimmed || !isSafeProofSourceRef(trimmed)) return null;
  if (trimmed.startsWith('/tpb-internal')) {
    return convertInternalHref(trimmed);
  }
  return trimmed;
}
